import { FC, useRef, useState } from "react";
import styles from "./StockOverview.module.scss";
import StockInfo from "@/components/stock-info/StockInfo";
import Module from "@/components/module/Module";
import StockStamps from "@/components/stock-stamps/StockStamps";
import { Stock } from "@/models/stock";
import { useAtomValue } from "jotai";
import { backgroundColorState, dividerState } from "@/lib/store";
import dynamic from "next/dynamic";
import TimelineIcon from "@mui/icons-material/Timeline";
import Button from "@mui/material/Button";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import MUISkeleton from "@mui/material/Skeleton";
import { useNearViewport } from "src/hooks/useNearViewport";
import { classNames } from "@/lib/utils";
import { Typography, useMediaQuery } from "@mui/material";
import { MemoDataGridNews } from "@/components/data-grid-news/DataGridNews";
import StockInfoDetails from "../stock-info/StockInfoDetails";
import StockChainSelector from "../stock-info/StockChainSelector";
import { ErrorBoundary } from "react-error-boundary";
import ModuleErrorFallback from "../error-fallback/ModuleErrorFallback";
import StockCommentary from "../stock-commentary/StockCommentary";
import StockLockupCallout from "../stock-lockup/StockLockupCallout";
import DeferredMount from "../deferred-mount/DeferredMount";

const StockChart = dynamic(() => import("@/components/ortex-charts/StockChart"), { ssr: false });

const newsOptions = { stock_news: true, type: "company" as "company" };

// The news list is fetched and built once it is near the viewport. On a phone it is the last block,
// about two screens down, and its first page of cards was one of the start-up's longest renders; it
// shows the same skeleton as while it loads until then. On larger screens it is in view at once.
const StockNews: FC<{ id: number }> = ({ id }) => {
    const placeholder = useRef<HTMLSpanElement>(null);
    const near = useNearViewport(placeholder);
    return near ? <MemoDataGridNews id={id} options={newsOptions} sortBy="importance" /> : <MUISkeleton ref={placeholder} variant="rounded" width="100%" height="100%" />;
};

const StockOverview: FC<{ stock: Stock }> = ({ stock }) => {
    const backgroundColor = useAtomValue(backgroundColorState);
    const dividerColor = useAtomValue(dividerState);

    const [openModal, setOpenModal] = useState<boolean>(false);
    // The phone's chart lives in a SwipeableDrawer, which keeps its contents mounted while closed:
    // the whole chart (its code, a year of bars, today's minute candles off the socket) was built
    // on every phone visit behind a "Show Graph" button. It is built on the first open instead and
    // kept from then on.
    const [chartOpened, setChartOpened] = useState<boolean>(false);

    const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

    const toggleDrawer = (newOpen: boolean) => () => {
        if (newOpen) setChartOpened(true);
        setOpenModal(newOpen);
    };

    const isETF = stock.type === 3;

    const isDesktop = useMediaQuery("(min-width: 768px)");

    return (
        <Box className={classNames(styles.StockOverview, isETF ? styles.etfOverview : null)}>
            {/*
             * Note: the legacy ``<StockReportCallout/>`` lived here and
             * surfaced "Stock Report available" as a banner above the
             * data-highlights module. That entry point moved to the
             * top-bar ``<StockReportBadge/>`` (in RootLayout) and the
             * new "Stock Report" tab (in StockLinks); the in-page
             * banner is now redundant and was removed to avoid showing
             * the same CTA three times when a report exists.
             */}
            <div className={styles.info}>
                <Module
                    title={
                        <Box className={styles["info-title-container"]}>
                            <Typography variant="h2">ORTEX Data Highlights</Typography>
                            {isDesktop && <StockInfo stock={stock} isCustomView={false} />}
                        </Box>
                    }
                    help={{ moduleId: "morning_note", modulePage: "V2_markets_overview" }}
                    subHeader={!isDesktop && <StockInfo stock={stock} isCustomView={false} />}
                >
                    <Box className={styles["highlights"]}>
                        <Box sx={{ gridArea: "a" }}>
                            {/*
                             * Lock-up expiry callout. Sits above the commentary rather
                             * than in its own grid row: the overview's row templates are
                             * fixed percentages per breakpoint, so a conditional row
                             * would leave a gap on the ~99% of stocks with no expiry
                             * ahead. This area already scrolls, and a dated, quantified
                             * supply event is exactly what "ORTEX Data Highlights" is
                             * for. Renders nothing when there is no lock-up.
                             */}
                            <StockLockupCallout companyId={stock.company_id} exchange={stock.exchangesymbol} ticker={stock.ticker} stockId={stock.stock_id} />
                            <StockCommentary stock={stock} />
                        </Box>
                        <StockInfoDetails stock={stock} />

                        <StockChainSelector stock={stock} />
                    </Box>
                </Module>
            </div>
            <div className={styles.chart}>
                <Module noHeader noPadding={{ type: "all", source: "both" }} noBackground>
                    {isDesktop && <StockChart stock={stock} mode="overview" chartId="overview" key={stock.tradingitem_id} />}
                </Module>
            </div>
            <div className={styles.chartMobile}>
                <Button variant="outlined" endIcon={<TimelineIcon />} onClick={toggleDrawer(true)} sx={{ width: "100%" }}>
                    Show Graph
                </Button>
                {/* Closed, the drawer is an empty, hidden sheet (and on desktop this whole block is
                    display: none): a browser render mounts it just after hydration. */}
                <DeferredMount when="hydrated">
                    <SwipeableDrawer
                        onClose={toggleDrawer(false)}
                        onOpen={toggleDrawer(true)}
                        open={openModal}
                        disableBackdropTransition={!iOS}
                        disableDiscovery={iOS}
                        anchor="bottom"
                        sx={{
                            "& .MuiPaper-root": {
                                height: "100vh",
                                borderRadius: "8px 8px 0 0",
                                top: "env(safe-area-inset-top)",
                            },
                        }}
                    >
                        <Box
                            style={{
                                position: "absolute",
                                top: 40,
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                                visibility: "visible",
                                right: 0,
                                left: 0,
                                backgroundColor,
                                height: "calc(95% - env(safe-area-inset-top))",
                            }}
                        >
                            <div style={{ width: 30, height: 6, borderRadius: 3, position: "absolute", top: -24, left: "calc(50% - 15px)", backgroundColor: dividerColor }} />
                            {!isDesktop && chartOpened && <StockChart stock={stock} mode="overview" chartId="overview" key={stock.tradingitem_id} />}
                        </Box>
                    </SwipeableDrawer>
                </DeferredMount>
            </div>
            <ErrorBoundary
                FallbackComponent={() => (
                    <div style={{ gridColumn: "1 / -1", width: "100%" }}>
                        <ModuleErrorFallback />
                    </div>
                )}
                onError={(error: Error, info: { componentStack: string }) => {
                    console.error(error, info);
                }}
            >
                <StockStamps id={stock.stock_id} exchange={stock.exchangesymbol} ticker={stock.ticker} isETF={isETF} />
            </ErrorBoundary>
            <div className={styles.news}>
                <Module title="News" help={{ moduleId: "morning_note", modulePage: "V2_markets_overview" }} noPadding={{ source: "body", type: "all" }}>
                    <div className={styles["news-list"]}>
                        <StockNews id={stock.company_id} />
                    </div>
                </Module>
            </div>
        </Box>
    );
};

export default StockOverview;
